import fullColorLogo from '@/assets/logo/full_color_rect.png';
import i18n from '@/translations/config';
import { Card } from '@/types/card';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Type pour l'API File System Access (pas nécessaire dans notre implémentation actuelle)
declare global {
  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<FileSystemFileHandle>;
  }
}

// Options configurables pour le QR code
interface QrCodeOptions {
  color?: string;
  logoImage?: string;
  size?: number;
}

// Configuration complète pour le générateur de QR code
interface QrCodeConfig {
  value: string;
  size: number;
  bgColor: string;
  fgColor: string;
  level: 'L' | 'M' | 'Q' | 'H';
  imageSettings: {
    src: string;
    height: number;
    width: number;
    excavate: boolean;
  };
}

// Interface du store Zustand
interface QrCodeStore {
  qrCodeOptions: QrCodeOptions;
  updateQrCodeOptions: (options: Partial<QrCodeOptions>) => void;
  generateQrCodeConfig: (slug: string, color: string | null) => QrCodeConfig;
  downloadQrCode: (
    svg: SVGSVGElement | null,
    fileName?: string,
    card?: Card,
  ) => Promise<void>;
  shareQrCode: (svg: SVGSVGElement | null, card: Card) => Promise<void>;
  _generateEnhancedQrCodeBlob: (
    svg: SVGSVGElement,
    card?: Card,
  ) => Promise<Blob | null>;
}

// Constantes pour éviter la duplication
const DEFAULT_QR_COLOR = '#000000';
const DEFAULT_QR_SIZE = 256;
const CANVAS_WIDTH = 1000;
const CANVAS_HEIGHT = 1200;
const LINKO_DARK = '#063775';
const LINKO_MEDIUM = '#2193b0';
const LINKO_LIGHT = '#E6F7FF';
const TIMEOUT_LONG = 3000;
const DOWNLOAD_CLEANUP_DELAY = 200;

// Création du store avec persistence
export const useQrCodeStore = create<QrCodeStore>()(
  persist(
    (set, get) => ({
      qrCodeOptions: {
        color: DEFAULT_QR_COLOR,
        logoImage: fullColorLogo,
        size: DEFAULT_QR_SIZE,
      },

      // --- NEW HELPER FUNCTION ---
      /**
       * Generates the enhanced QR code image with styling onto a canvas and returns it as a Blob.
       * @param svg The SVG element of the base QR code.
       * @param card The card data for the title.
       * @returns A Promise resolving to the image Blob, or null if generation fails.
       */
      _generateEnhancedQrCodeBlob: async (
        svg: SVGSVGElement,
        card?: Card,
      ): Promise<Blob | null> => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            console.error('Failed to get canvas context');
            toast.error(i18n.t('cardsList:share.errorGeneratingImage')); // Use a more specific key if needed
            return null;
          }

          // Configuration des dimensions
          canvas.width = CANVAS_WIDTH;
          canvas.height = CANVAS_HEIGHT;

          // Création et application du dégradé de fond
          const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
          gradient.addColorStop(0, '#ffffff');
          gradient.addColorStop(1, LINKO_LIGHT);
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

          // Ajout d'une bordure avec dégradé
          const borderGradient = ctx.createLinearGradient(
            0,
            0,
            0,
            CANVAS_HEIGHT,
          );
          borderGradient.addColorStop(0, LINKO_DARK);
          borderGradient.addColorStop(1, LINKO_LIGHT);
          ctx.strokeStyle = borderGradient;
          ctx.lineWidth = 10;
          ctx.strokeRect(10, 10, CANVAS_WIDTH - 20, CANVAS_HEIGHT - 20);

          // Conversion du SVG en URL de données
          const svgString = new XMLSerializer().serializeToString(svg);
          const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
            svgString,
          )}`;

          // Création de l'image pour le QR code
          const qrCodeImg = new Image();

          // Chargement du QR code avec une promesse
          await new Promise<void>((resolve, reject) => {
            qrCodeImg.onload = () => resolve();
            qrCodeImg.onerror = () => {
              console.error('Failed to load SVG for QR code');
              reject(new Error('Failed to load QR code'));
            };
            qrCodeImg.src = svgUrl;
            setTimeout(
              () => reject(new Error('QR code load timeout')),
              TIMEOUT_LONG,
            );
          }).catch(error => {
            console.error('QR code loading error:', error);
            throw new Error('Failed to load QR code image');
          });

          // Calcul du positionnement du QR code
          const qrSize = Math.min(CANVAS_WIDTH * 0.7, CANVAS_HEIGHT * 0.5);
          const qrX = (CANVAS_WIDTH - qrSize) / 2;
          const qrY = CANVAS_HEIGHT * 0.3;

          // Dessin du QR code
          ctx.drawImage(qrCodeImg, qrX, qrY, qrSize, qrSize);

          // --- Draw the logo in the center of the QR code ---
          const { qrCodeOptions } = get();
          const logoSrc = qrCodeOptions.logoImage || fullColorLogo;
          const logoSize = qrSize * 0.2; // 20% of QR code size
          const logoX = qrX + (qrSize - logoSize) / 2;
          const logoY = qrY + (qrSize - logoSize) / 2;

          // Draw logo image after QR code
          await new Promise<void>(resolve => {
            const logoImg = new window.Image();
            logoImg.onload = () => {
              ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
              resolve();
            };
            logoImg.onerror = () => {
              console.warn('Could not load logo image for QR code overlay');
              resolve(); // Continue even if logo fails
            };
            logoImg.src = logoSrc;
          });
          // --- End logo drawing ---

          // Ajout du titre avec dégradé
          const titleGradient = ctx.createLinearGradient(
            0,
            CANVAS_HEIGHT * 0.1,
            0,
            CANVAS_HEIGHT * 0.2,
          );
          titleGradient.addColorStop(0, LINKO_DARK);
          titleGradient.addColorStop(1, LINKO_MEDIUM);
          ctx.font = 'bold 40px Arial';
          ctx.fillStyle = titleGradient;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const cardName =
            card?.full_name || i18n.t('cardsList:share.defaultCardName');
          const titleText = i18n.t('cardsList:share.businessCardOf', {
            name: cardName,
          });
          const titleWidth = ctx.measureText(titleText).width;

          if (titleWidth > CANVAS_WIDTH * 0.8) {
            ctx.font = 'bold 35px Arial';
            const nameText = cardName;
            const prefix = i18n.t('cardsList:share.businessCardPrefix');
            ctx.fillText(prefix, CANVAS_WIDTH / 2, CANVAS_HEIGHT * 0.1);
            ctx.fillText(nameText, CANVAS_WIDTH / 2, CANVAS_HEIGHT * 0.15);
          } else {
            ctx.fillText(titleText, CANVAS_WIDTH / 2, CANVAS_HEIGHT * 0.12);
          }

          // Ajout du slogan avec dégradé
          const sloganGradient = ctx.createLinearGradient(
            0,
            CANVAS_HEIGHT * 0.8,
            0,
            CANVAS_HEIGHT * 0.9,
          );
          sloganGradient.addColorStop(0, LINKO_MEDIUM);
          sloganGradient.addColorStop(1, LINKO_LIGHT);
          ctx.font = '30px Arial';
          ctx.fillStyle = sloganGradient;
          ctx.fillText(
            i18n.t('cardsList:share.slogan'),
            CANVAS_WIDTH / 2,
            CANVAS_HEIGHT * 0.85,
          );

          // Ajout de l'URL du site
          ctx.font = '24px Arial';
          ctx.fillStyle = LINKO_DARK;
          ctx.fillText(
            'linko-connect.com',
            CANVAS_WIDTH / 2,
            CANVAS_HEIGHT * 0.92,
          );

          // Convert canvas to Blob
          return new Promise<Blob | null>(resolve => {
            canvas.toBlob(
              blob => {
                resolve(blob);
              },
              'image/png',
              0.9, // Quality setting
            );
          });
        } catch (error) {
          console.error('Error generating enhanced QR code Blob:', error);
          toast.error(i18n.t('cardsList:share.errorGeneratingImage'));
          return null;
        }
      },
      // --- END HELPER FUNCTION ---

      updateQrCodeOptions: (options: Partial<QrCodeOptions>) => {
        set(state => ({
          qrCodeOptions: {
            ...state.qrCodeOptions,
            ...options,
          },
        }));
      },

      generateQrCodeConfig: (
        slug: string,
        color: string | null,
      ): QrCodeConfig => {
        const { qrCodeOptions } = get();
        const url = `${window.location.origin}/${i18n.language}/profile/${slug}`;

        const fgColor = color ?? DEFAULT_QR_COLOR;
        const size = qrCodeOptions.size || DEFAULT_QR_SIZE;
        const logoSrc = qrCodeOptions.logoImage || fullColorLogo;

        return {
          value: url,
          size: size,
          bgColor: '#ffffff',
          fgColor: fgColor,
          level: 'H',
          imageSettings: {
            src: logoSrc,
            height: size * 0.15,
            width: size * 0.15,
            excavate: true,
          },
        };
      },

      /**
       * Télécharge un QR code avec design amélioré
       * @param svg L'élément SVG du QR code
       * @param fileName Nom du fichier à télécharger
       * @param card Informations de la carte pour le titre
       */
      downloadQrCode: async (
        svg: SVGSVGElement | null,
        fileName: string = 'qrcode',
        card?: Card,
      ) => {
        if (!svg) {
          toast.error(i18n.t('cardsList:share.errorDownloading'));
          return;
        }

        try {
          // Generate the image blob using the helper function
          const blob = await get()._generateEnhancedQrCodeBlob(svg, card);

          if (!blob) {
            // Error handling is done within the helper function
            return;
          }

          // Proceed with download using the blob
          try {
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `${fileName}.png`;

            document.body.appendChild(downloadLink);
            downloadLink.click();

            setTimeout(() => {
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(url);
              toast.success(i18n.t('cardsList:share.downloaded'));
            }, DOWNLOAD_CLEANUP_DELAY);
          } catch (downloadError) {
            console.error('Blob download failed:', downloadError);
            toast.error(i18n.t('cardsList:share.errorDownloading'));
            // Optional: Fallback to data URL method if needed, though blob is generally preferred
          }
        } catch (error) {
          toast.error(i18n.t('cardsList:share.errorDownloading'));
        }
      },

      /**
       * Partage un QR code via Web Share API ou le télécharge
       * @param svg L'élément SVG du QR code
       * @param card Informations de la carte pour le partage
       */
      shareQrCode: async (svg: SVGSVGElement | null, card: Card) => {
        try {
          if (!svg || !card) {
            toast.error(i18n.t('cardsList:share.errorSharing'));
            return;
          }

          const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            );
          const cardUrl = `${window.location.origin}/${i18n.language}/profile/${card.slug}`;
          const fileName = `linko-card-${card.slug || 'shared'}.png`;

          // Sur mobile, essayer de partager l'image via l'API Web Share
          if (isMobile) {
            // 1. Générer le Blob de l'image améliorée
            const blob = await get()._generateEnhancedQrCodeBlob(svg, card);
            if (!blob) {
              // L'erreur est déjà gérée dans _generateEnhancedQrCodeBlob
              return;
            }

            // 2. Créer un objet File à partir du Blob
            const file = new File([blob], fileName, { type: 'image/png' });

            // 3. Vérifier si le partage de fichiers est possible
            if (
              navigator.share &&
              navigator.canShare &&
              navigator.canShare({ files: [file] })
            ) {
              try {
                // 4. Tenter le partage avec le fichier
                await navigator.share({
                  title: card.full_name || 'Linko Card',
                  text: i18n.t('cardsList:share.shareText', {
                    cardName: card.full_name,
                  }),
                  url: cardUrl, // Inclure l'URL est une bonne pratique
                  files: [file],
                });
                toast.success(i18n.t('cardsList:share.shared'));
                return;
              } catch (shareError: unknown) {
                return;
              }
            } else {
              console.warn(
                'Web Share API does not support file sharing or is unavailable.',
              );
              // Passer au fallback
            }

            try {
              const url = URL.createObjectURL(blob);
              const downloadLink = document.createElement('a');
              downloadLink.href = url;
              downloadLink.download = fileName;
              document.body.appendChild(downloadLink);
              downloadLink.click();
              setTimeout(() => {
                document.body.removeChild(downloadLink);
                URL.revokeObjectURL(url);
                // Utiliser une clé de traduction générique ou spécifique au téléchargement
                toast.success(i18n.t('cardsList:share.downloaded'));
              }, DOWNLOAD_CLEANUP_DELAY);
              return; // Téléchargement de fallback initié
            } catch (downloadError) {
              console.error('Fallback mobile download failed:', downloadError);
              toast.error(i18n.t('cardsList:share.errorDownloading'));
              return;
            }
          } else {
            // Sur desktop: utiliser la fonction de téléchargement standard
            get().downloadQrCode(svg, `linko-card-${card.slug}`, card);
          }
        } catch (error) {
          console.error('Share failed unexpectedly:', error);
          toast.error(i18n.t('cardsList:share.errorSharing'));
        }
      },
    }),
    {
      name: 'qr-code-store', // nom unique pour localStorage
      partialize: state => ({ qrCodeOptions: state.qrCodeOptions }), // ne persister que qrCodeOptions
    },
  ),
);

// Hook personnalisé pour faciliter l'utilisation du store
export const useQrCode = () => {
  const qrCodeOptions = useQrCodeStore(state => state.qrCodeOptions);
  const updateQrCodeOptions = useQrCodeStore(
    state => state.updateQrCodeOptions,
  );
  const generateQrCodeConfig = useQrCodeStore(
    state => state.generateQrCodeConfig,
  );
  const downloadQrCode = useQrCodeStore(state => state.downloadQrCode);
  const shareQrCode = useQrCodeStore(state => state.shareQrCode);

  return {
    qrCodeOptions,
    updateQrCodeOptions,
    generateQrCodeConfig,
    downloadQrCode,
    shareQrCode,
  };
};
